import React, { useState, useEffect } from 'react'
import { Modal, Button, Form, InputGroup, Badge, Spinner, Col, Row } from 'react-bootstrap'
import { useLazyQuery, useMutation, useQuery } from '@apollo/client'
import { toast } from 'react-toastify'
import { GET_ALL_RECIPIENTS } from '../../../gql/queries/contactsApiQueries'
import { ADD_CAMPAIGN_TARGETS_BY_FILTER } from '../../../gql/mutations/campaigns'
import { GET_ALL_CARRIERS_LIST } from '../../../gql/queries/carriersQuery'
import { GET_ALL_STATES_EU_LIST } from '../../../gql/queries/statesEUQuery'
import { IResRecipientsAPI, NodeContact } from '../../../types/contactsApi'
import { PhoneProviderActions } from '../../../components/telephony/PhoneProviderActions'
import { CSVImportModal } from './CSVImportModal'
import './RecipientSelectorModal.css'

const POLICY_STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'EXPIRED', label: 'Expired' },
  { value: 'CANCELLED', label: 'Cancelled' },
  { value: 'PENDING', label: 'Pending' },
]

interface ActiveFilters {
  state: string
  carrier: string
  policyStatus: string
  isPolicyHolder: boolean | undefined
}

const EMPTY_FILTERS: ActiveFilters = {
  state: '',
  carrier: '',
  policyStatus: '',
  isPolicyHolder: undefined,
}

interface RecipientSelectorModalProps {
  show: boolean
  onHide: () => void
  initialSelectedIds: string[]
  onSave: (selectedContactIds: string[]) => void
  confirmLabel?: string
  campaignId?: string
  onBulkAdded?: () => void
}

export const RecipientSelectorModal: React.FC<RecipientSelectorModalProps> = ({
  show,
  onHide,
  initialSelectedIds,
  onSave,
  confirmLabel,
  campaignId,
  onBulkAdded,
}) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [selectedIds, setSelectedIds] = useState<string[]>(initialSelectedIds)
  const [highlightedAvailable, setHighlightedAvailable] = useState<string[]>([])
  const [highlightedSelected, setHighlightedSelected] = useState<string[]>([])
  const [showCSVImport, setShowCSVImport] = useState(false)
  const [showFilters, setShowFilters] = useState(false)
  const [pendingFilters, setPendingFilters] = useState<ActiveFilters>(EMPTY_FILTERS)
  const [activeFilters, setActiveFilters] = useState<ActiveFilters>(EMPTY_FILTERS)
  const [knownContacts, setKnownContacts] = useState<Record<string, NodeContact>>({})

  const activeFilterCount = Object.values(activeFilters).filter(
    (v) => v !== '' && v !== undefined
  ).length

  // Dropdown data
  const { data: carriersData } = useQuery(GET_ALL_CARRIERS_LIST, { skip: !show })
  const { data: statesData } = useQuery(GET_ALL_STATES_EU_LIST, { skip: !show })

  const carriers: { id: string; name: string }[] = carriersData?.allInsuranceOperatorsList ?? []
  const states: { id: string; name: string; code: string }[] = statesData?.allStatesList ?? []

  const [getRecipients, { data, loading, error }] = useLazyQuery<IResRecipientsAPI>(
    GET_ALL_RECIPIENTS,
    { fetchPolicy: 'network-only' }
  )

  const [getContactsById, { data: dataById }] = useLazyQuery<IResRecipientsAPI>(
    GET_ALL_RECIPIENTS,
    { fetchPolicy: 'network-only' }
  )

  const [addByFilter, { loading: addingByFilter }] = useMutation(ADD_CAMPAIGN_TARGETS_BY_FILTER)

  // Update known contacts cache
  useEffect(() => {
    if (data?.allRecipients?.edges) {
      setKnownContacts((prev) => {
        const next = { ...prev }
        data.allRecipients.edges.forEach((edge) => { next[edge.node.id] = edge.node })
        return next
      })
    }
  }, [data])

  useEffect(() => {
    if (dataById?.allRecipients?.edges) {
      setKnownContacts((prev) => {
        const next = { ...prev }
        dataById.allRecipients.edges.forEach((edge) => { next[edge.node.id] = edge.node })
        return next
      })
    }
  }, [dataById])

  // Fetch initial selected contacts if missing from cache
  useEffect(() => {
    if (show && initialSelectedIds.length > 0) {
      const missingIds = initialSelectedIds.filter((id) => !knownContacts[id])
      if (missingIds.length > 0) {
        getContactsById({ variables: { ids: missingIds.join(','), first: missingIds.length } })
      }
    }
  }, [show, initialSelectedIds, knownContacts, getContactsById])

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 300)
    return () => clearTimeout(timer)
  }, [searchTerm])

  // Fetch contacts when search or active filters change
  useEffect(() => {
    if (show) {
      getRecipients({
        variables: {
          name: debouncedSearch || undefined,
          state: activeFilters.state || undefined,
          carrier: activeFilters.carrier || undefined,
          policyStatus: activeFilters.policyStatus || undefined,
          isPolicyHolder: activeFilters.isPolicyHolder,
          first: 100,
          offset: 0,
        },
      })
    }
  }, [debouncedSearch, activeFilters, show, getRecipients])

  // Reset on open
  useEffect(() => {
    if (show) {
      setSelectedIds(initialSelectedIds)
      setHighlightedAvailable([])
      setHighlightedSelected([])
      setSearchTerm('')
      setShowCSVImport(false)
      setShowFilters(false)
      setPendingFilters(EMPTY_FILTERS)
      setActiveFilters(EMPTY_FILTERS)
    }
  }, [show, initialSelectedIds])

  const allRecipients = data?.allRecipients?.edges ?? []
  const totalCount: number = (data?.allRecipients as any)?.totalCount ?? 0
  const availableContacts = allRecipients.filter((edge) => !selectedIds.includes(edge.node.id))
  const selectedContacts = selectedIds.map((id) => knownContacts[id]).filter(Boolean)

  // Move handlers
  const moveToSelected = () => {
    setSelectedIds([...selectedIds, ...highlightedAvailable])
    setHighlightedAvailable([])
  }
  const moveToAvailable = () => {
    setSelectedIds(selectedIds.filter((id) => !highlightedSelected.includes(id)))
    setHighlightedSelected([])
  }
  const moveAllToSelected = () => {
    const allIds = availableContacts.map((edge) => edge.node.id)
    setSelectedIds([...selectedIds, ...allIds])
  }
  const moveAllToAvailable = () => {
    setSelectedIds([])
    setHighlightedSelected([])
  }

  const toggleAvailableHighlight = (id: string) => {
    setHighlightedAvailable((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    )
  }
  const toggleSelectedHighlight = (id: string) => {
    setHighlightedSelected((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    )
  }

  const handleApplyFilters = () => {
    setActiveFilters({ ...pendingFilters })
    setShowFilters(false)
  }

  const handleClearFilters = () => {
    setPendingFilters(EMPTY_FILTERS)
    setActiveFilters(EMPTY_FILTERS)
  }

  const handleAddAllMatching = async () => {
    if (!campaignId) return
    try {
      const { data: result } = await addByFilter({
        variables: {
          campaignId,
          name: debouncedSearch || undefined,
          state: activeFilters.state || undefined,
          carrier: activeFilters.carrier || undefined,
          policyStatus: activeFilters.policyStatus || undefined,
          isPolicyHolder: activeFilters.isPolicyHolder,
        },
      })
      const added = result?.addCampaignTargetsByFilter?.addedCount ?? 0
      const skipped = result?.addCampaignTargetsByFilter?.skippedCount ?? 0
      toast.success(
        `${added} contact${added !== 1 ? 's' : ''} added as targets${skipped > 0 ? ` (${skipped} already existed)` : ''}`
      )
      onBulkAdded?.()
      onHide()
    } catch {
      toast.error('Could not add all matching contacts')
    }
  }

  const handleSave = () => onSave(selectedIds)

  const handleImportSuccess = (importedContactIds: string[]) => {
    const combined = [...selectedIds, ...importedContactIds]
    setSelectedIds(combined.filter((id, i) => combined.indexOf(id) === i))
    getRecipients({ variables: { name: debouncedSearch || undefined, first: 100, offset: 0 } })
  }

  const renderContactItem = (
    contact: NodeContact,
    isHighlighted: boolean,
    onToggle: () => void
  ) => (
    <div
      key={contact.id}
      className={`recipient-item ${isHighlighted ? 'highlighted' : ''}`}
      onClick={onToggle}
    >
      <div className='form-check'>
        <input
          className='form-check-input'
          type='checkbox'
          checked={isHighlighted}
          onChange={onToggle}
          onClick={(e) => e.stopPropagation()}
        />
        <div className='recipient-info'>
          <div className='fw-bold'>
            {contact.fullName || `${contact.firstName} ${contact.lastName}`}
          </div>
          <div className='text-muted small'>
            {contact.email && <span className='me-3'>{contact.email}</span>}
            {contact.phone && (
              <PhoneProviderActions
                phone={contact.phone}
                stopPropagation={false}
                className='d-inline-flex flex-column align-items-start'
                textClassName='text-muted small'
              />
            )}
          </div>
        </div>
      </div>
    </div>
  )

  return (
    <Modal show={show} onHide={onHide} size='xl' centered>
      <Modal.Header closeButton>
        <Modal.Title>Select Campaign Recipients</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Filters panel */}
        {showFilters && (
          <div className='border rounded p-4 mb-4 bg-light'>
            <div className='d-flex justify-content-between align-items-center mb-3'>
              <span className='fw-semibold'>Filters</span>
              <Button variant='link' size='sm' className='p-0 text-muted' onClick={() => setShowFilters(false)}>
                <i className='bi bi-x-lg'></i>
              </Button>
            </div>
            <Row className='g-3'>
              <Col md={3}>
                <Form.Label className='small fw-semibold'>State</Form.Label>
                <Form.Select
                  size='sm'
                  value={pendingFilters.state}
                  onChange={(e) => setPendingFilters((f) => ({ ...f, state: e.target.value }))}
                >
                  <option value=''>All states</option>
                  {states.map((s) => (
                    <option key={s.id} value={s.code}>{s.name} ({s.code})</option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={3}>
                <Form.Label className='small fw-semibold'>Carrier</Form.Label>
                <Form.Select
                  size='sm'
                  value={pendingFilters.carrier}
                  onChange={(e) => setPendingFilters((f) => ({ ...f, carrier: e.target.value }))}
                >
                  <option value=''>All carriers</option>
                  {carriers.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={3}>
                <Form.Label className='small fw-semibold'>Policy status</Form.Label>
                <Form.Select
                  size='sm'
                  value={pendingFilters.policyStatus}
                  onChange={(e) => setPendingFilters((f) => ({ ...f, policyStatus: e.target.value }))}
                >
                  <option value=''>Any status</option>
                  {POLICY_STATUS_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={3}>
                <Form.Label className='small fw-semibold'>Policy holder</Form.Label>
                <Form.Select
                  size='sm'
                  value={
                    pendingFilters.isPolicyHolder === true
                      ? 'yes'
                      : pendingFilters.isPolicyHolder === false
                        ? 'no'
                        : ''
                  }
                  onChange={(e) =>
                    setPendingFilters((f) => ({
                      ...f,
                      isPolicyHolder:
                        e.target.value === 'yes' ? true : e.target.value === 'no' ? false : undefined,
                    }))
                  }
                >
                  <option value=''>All contacts</option>
                  <option value='yes'>Has policy</option>
                  <option value='no'>No policy</option>
                </Form.Select>
              </Col>
            </Row>
            <div className='d-flex gap-2 mt-3'>
              <Button size='sm' variant='primary' onClick={handleApplyFilters}>
                Apply filters
              </Button>
              <Button size='sm' variant='light' onClick={handleClearFilters}>
                Clear
              </Button>
            </div>
          </div>
        )}

        <div className='recipient-selector-container'>
          {/* Available Column */}
          <div className='recipient-column'>
            <div className='column-header'>
              <div className='d-flex align-items-center justify-content-between mb-2'>
                <h6 className='mb-0'>
                  Available Contacts
                  {totalCount > 0 && (
                    <Badge bg='secondary' pill className='ms-2'>{totalCount}</Badge>
                  )}
                </h6>
                <Button
                  size='sm'
                  variant={activeFilterCount > 0 ? 'secondary' : 'light'}
                  onClick={() => { setShowFilters((v) => !v); setPendingFilters({ ...activeFilters }) }}
                >
                  <i className='bi bi-funnel me-1'></i>
                  Filters
                  {activeFilterCount > 0 && (
                    <Badge bg='light' text='primary' pill className='ms-1'>{activeFilterCount}</Badge>
                  )}
                </Button>
              </div>
              <div className='d-flex gap-2 mb-3'>
                <InputGroup style={{ flex: 1 }}>
                  <Form.Control
                    type='text'
                    placeholder='Search contacts…'
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  {loading && (
                    <InputGroup.Text>
                      <Spinner animation='border' size='sm' />
                    </InputGroup.Text>
                  )}
                </InputGroup>
                <Button
                  variant='outline-success'
                  size='sm'
                  onClick={() => setShowCSVImport(true)}
                  title='Import contacts from CSV'
                >
                  <i className='bi bi-file-earmark-arrow-up me-1'></i>
                  CSV
                </Button>
              </div>
              {/* Active filter chips */}
              {activeFilterCount > 0 && (
                <div className='d-flex flex-wrap gap-1 mb-2'>
                  {activeFilters.state && (
                    <Badge bg='primary' className='fw-normal'>
                      State: {activeFilters.state}
                      <i
                        className='bi bi-x ms-1'
                        style={{ cursor: 'pointer' }}
                        onClick={() => setActiveFilters((f) => ({ ...f, state: '' }))}
                      ></i>
                    </Badge>
                  )}
                  {activeFilters.carrier && (
                    <Badge bg='primary' className='fw-normal'>
                      Carrier: {carriers.find((c) => c.id === activeFilters.carrier)?.name ?? activeFilters.carrier}
                      <i
                        className='bi bi-x ms-1'
                        style={{ cursor: 'pointer' }}
                        onClick={() => setActiveFilters((f) => ({ ...f, carrier: '' }))}
                      ></i>
                    </Badge>
                  )}
                  {activeFilters.policyStatus && (
                    <Badge bg='primary' className='fw-normal'>
                      Status: {activeFilters.policyStatus}
                      <i
                        className='bi bi-x ms-1'
                        style={{ cursor: 'pointer' }}
                        onClick={() => setActiveFilters((f) => ({ ...f, policyStatus: '' }))}
                      ></i>
                    </Badge>
                  )}
                  {activeFilters.isPolicyHolder !== undefined && (
                    <Badge bg='primary' className='fw-normal'>
                      {activeFilters.isPolicyHolder ? 'Has policy' : 'No policy'}
                      <i
                        className='bi bi-x ms-1'
                        style={{ cursor: 'pointer' }}
                        onClick={() => setActiveFilters((f) => ({ ...f, isPolicyHolder: undefined }))}
                      ></i>
                    </Badge>
                  )}
                </div>
              )}
            </div>

            <div className='contacts-list'>
              {error ? (
                <div className='text-center text-danger p-4 small'>
                  <i className='bi bi-exclamation-triangle me-2'></i>
                  {error.message}
                </div>
              ) : loading && availableContacts.length === 0 ? (
                <div className='text-center p-4'>
                  <Spinner animation='border' size='sm' />
                </div>
              ) : availableContacts.length === 0 ? (
                <div className='text-center text-muted p-4'>
                  <i className='bi bi-inbox fs-1 d-block mb-2'></i>
                  No contacts found
                </div>
              ) : (
                availableContacts.map(({ node: contact }) =>
                  renderContactItem(
                    contact,
                    highlightedAvailable.includes(contact.id),
                    () => toggleAvailableHighlight(contact.id)
                  )
                )
              )}
            </div>
          </div>

          {/* Control Buttons */}
          <div className='control-buttons'>
            <Button
              variant='outline-primary'
              size='sm'
              onClick={moveToSelected}
              disabled={highlightedAvailable.length === 0}
              title='Move selected'
            >
              <i className='bi bi-chevron-right'></i>
            </Button>
            <Button
              variant='outline-primary'
              size='sm'
              onClick={moveAllToSelected}
              disabled={availableContacts.length === 0}
              title='Move all visible'
            >
              <i className='bi bi-chevron-double-right'></i>
            </Button>
            <Button
              variant='outline-secondary'
              size='sm'
              onClick={moveToAvailable}
              disabled={highlightedSelected.length === 0}
              title='Remove selected'
            >
              <i className='bi bi-chevron-left'></i>
            </Button>
            <Button
              variant='outline-secondary'
              size='sm'
              onClick={moveAllToAvailable}
              disabled={selectedIds.length === 0}
              title='Remove all'
            >
              <i className='bi bi-chevron-double-left'></i>
            </Button>
          </div>

          {/* Selected Column */}
          <div className='recipient-column'>
            <div className='column-header'>
              <h6 className='mb-3'>
                Selected Recipients
                {selectedIds.length > 0 && (
                  <Badge bg='success' pill className='ms-2'>{selectedIds.length}</Badge>
                )}
              </h6>
              <div className='mb-3' style={{ height: '38px' }}>
                {selectedIds.length > 0 && (
                  <div className='alert alert-success mb-0 py-1 small'>
                    <i className='bi bi-check-circle me-2'></i>
                    {selectedIds.length} contact{selectedIds.length !== 1 && 's'} ready
                  </div>
                )}
              </div>
            </div>
            <div className='contacts-list'>
              {selectedContacts.length === 0 ? (
                <div className='text-center text-muted p-4'>
                  <i className='bi bi-person-plus fs-1 d-block mb-2'></i>
                  {selectedIds.length > 0 ? (
                    <span>
                      {selectedIds.length} recipients selected
                      <br />
                      <small className='text-muted'>(Details loading)</small>
                    </span>
                  ) : (
                    <span>
                      No recipients selected yet
                      <br />
                      <small>Select contacts from the left</small>
                    </span>
                  )}
                </div>
              ) : (
                selectedContacts.map((contact) =>
                  renderContactItem(
                    contact,
                    highlightedSelected.includes(contact.id),
                    () => toggleSelectedHighlight(contact.id)
                  )
                )
              )}
            </div>
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer className='justify-content-between'>
        {/* Bulk add — only shown when campaignId is provided */}
        {campaignId ? (
          <Button
            variant='outline-primary'
            onClick={handleAddAllMatching}
            disabled={addingByFilter || totalCount === 0}
            title='Add all contacts matching current search and filters directly to the campaign'
          >
            {addingByFilter ? (
              <Spinner animation='border' size='sm' className='me-2' />
            ) : (
              <i className='bi bi-people-fill me-2'></i>
            )}
            Add all matching
            {totalCount > 0 && <Badge bg='primary' pill className='ms-2'>{totalCount}</Badge>}
          </Button>
        ) : (
          <div />
        )}
        <div className='d-flex gap-2'>
          <Button variant='secondary' onClick={onHide}>Cancel</Button>
          <Button
            variant='primary'
            onClick={handleSave}
            disabled={selectedIds.length === 0}
          >
            {confirmLabel || `Save Selection (${selectedIds.length})`}
          </Button>
        </div>
      </Modal.Footer>

      <CSVImportModal
        show={showCSVImport}
        onHide={() => setShowCSVImport(false)}
        onImportSuccess={handleImportSuccess}
      />
    </Modal>
  )
}
